<template>
  <div class="dealer-privacy">
    <!-- Header Section -->
    <section class="privacy-hero">
      <v-container>
        <div class="hero-content">
          <div class="hero-icon">
            <i class="material-icons-round">security</i>
          </div>
          <h1 class="hero-title">🔒 AutoSyncX™ Privacy Policy</h1>
          <p class="effective-date">Effective Date: [Insert Date]</p>
        </div>
      </v-container>
    </section>

    <!-- Privacy Content -->
    <section class="privacy-content">
      <v-container>
        <div class="privacy-sections">
          
          <div class="privacy-section" v-for="section in privacySections" :key="section.id">
            <h2 class="section-title">{{ section.title }}</h2>
            <p class="section-description">{{ section.description }}</p>
          </div>

        </div>
      </v-container>
    </section>

    <!-- Cookie Policy Section -->
    <section id="cookies" class="cookie-section">
      <v-container>
        <div class="cookie-content">
          <h2 class="cookie-title">🍪 AutoSyncX™ Cookie Policy</h2>
          <p class="cookie-intro">Our website uses cookies to:</p>
          
          <div class="cookie-list">
            <div class="cookie-item" v-for="cookie in cookieUses" :key="cookie.id">
              <div class="cookie-bullet">•</div>
              <span class="cookie-text">{{ cookie.text }}</span>
            </div>
          </div>
          
          <p class="cookie-note">
            You can disable cookies in your browser, but certain features may not work properly.
          </p>
        </div>
      </v-container>
    </section>

    <!-- Compliance Section -->
    <section id="compliance" class="compliance-section">
      <v-container>
        <div class="compliance-content">
          <h2 class="compliance-title">✅ AutoSyncX™ Compliance Statement</h2>
          <p class="compliance-intro">AutoSyncX™ follows all applicable laws and industry standards, including:</p>
          
          <div class="compliance-list">
            <div class="compliance-item" v-for="compliance in complianceItems" :key="compliance.id">
              <div class="compliance-bullet">•</div>
              <span class="compliance-text">{{ compliance.text }}</span>
            </div>
          </div>
          
          <p class="compliance-note">
            By using AutoSyncX™, you acknowledge compliance with these rules and agree to operate in good faith within the platform.
          </p>
        </div>
      </v-container>
    </section>

    <!-- Back to Home -->
    <section class="back-section">
      <v-container>
        <div class="back-content">
          <v-btn
            class="back-btn"
            large
            outlined
            @click="goBack"
          >
            <i class="material-icons-round">arrow_back</i>
            Back to Home
          </v-btn>
        </div>
      </v-container>
    </section>
  </div>
</template>

<script>
export default {
  name: "DealerPrivacy",
  data() {
    return {
      privacySections: [
        {
          id: 1,
          title: 'Data Collected',
          description: 'We collect dealership information, inventory details, referral activity, payment history, and communication logs.'
        },
        {
          id: 2,
          title: 'How We Use Data',
          description: 'To facilitate sales referrals, calculate commissions, improve platform features, and provide customer support.'
        },
        {
          id: 3,
          title: 'Third-Party Sharing',
          description: 'Data is shared only with approved partners (payment processors, analytics tools, inventory integrations). We never sell personal data.'
        },
        {
          id: 4,
          title: 'Your Rights',
          description: 'Users may request data export or deletion at any time by contacting support@autosyncx.com.'
        },
        {
          id: 5,
          title: 'Security',
          description: 'AutoSyncX™ uses encryption and secure servers to protect sensitive data, including payment and dealership records.'
        }
      ],
      cookieUses: [
        {
          id: 1,
          text: 'Remember your login session'
        },
        {
          id: 2,
          text: 'Track referral performance'
        },
        {
          id: 3,
          text: 'Analyze usage for improvements'
        }
      ],
      complianceItems: [
        {
          id: 1,
          text: 'FTC Advertising Guidelines for transparent referral disclosures'
        },
        {
          id: 2,
          text: 'Data Protection Laws (CCPA, GDPR where applicable)'
        },
        {
          id: 3,
          text: 'Payment Compliance via secure processors (PCI-DSS standards)'
        }
      ]
    }
  },
  methods: {
    goBack() {
      // Navigate back to home page
      this.$router.push('/dealers');
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  },
  mounted() {
    // Handle hash scrolling when component is mounted
    if (this.$route.hash) {
      this.$nextTick(() => {
        const element = document.querySelector(this.$route.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    } else {
      // Scroll to top when component is mounted
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
</script>

<style scoped>
/* Privacy Page Styles */
.dealer-privacy {
  font-family: 'Inter', sans-serif;
  min-height: 100vh;
}

/* Hero Section */
.privacy-hero {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 80px 0 60px;
  text-align: center;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.hero-icon i {
  font-size: 2.5rem;
  color: white;
}

.hero-title {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 16px;
  line-height: 1.2;
}

.effective-date {
  font-size: 1rem;
  color: #6b7280;
  font-style: italic;
}

/* Privacy Content */
.privacy-content {
  padding: 80px 0;
  background: #ffffff;
}

.privacy-sections {
  max-width: 800px;
  margin: 0 auto;
}

.privacy-section {
  margin-bottom: 40px;
  padding: 30px;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid #28a745;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 16px;
}

.section-description {
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.7;
  margin: 0;
}

/* Cookie Section */
.cookie-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
}

.cookie-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.cookie-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 24px;
}

.cookie-intro {
  font-size: 1.2rem;
  color: #4a5568;
  margin-bottom: 32px;
}

.cookie-list {
  text-align: left;
  max-width: 600px;
  margin: 0 auto 32px;
}

.cookie-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}

.cookie-bullet {
  color: #f39c12;
  font-size: 1.5rem;
  margin-right: 12px;
  line-height: 1.2;
}

.cookie-text {
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.6;
}

.cookie-note {
  font-size: 1rem;
  color: #6b7280;
  font-style: italic;
  text-align: center;
}

/* Compliance Section */
.compliance-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}

.compliance-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.compliance-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 24px;
}

.compliance-intro {
  font-size: 1.2rem;
  color: #4a5568;
  margin-bottom: 32px;
}

.compliance-list {
  text-align: left;
  max-width: 700px;
  margin: 0 auto 32px;
}

.compliance-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}

.compliance-bullet {
  color: #28a745;
  font-size: 1.5rem;
  margin-right: 12px;
  line-height: 1.2;
}

.compliance-text {
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.6;
}

.compliance-note {
  font-size: 1.1rem;
  color: #4a5568;
  font-weight: 500;
  text-align: center;
  line-height: 1.6;
}

/* Back Section */
.back-section {
  padding: 60px 0;
  background: #ffffff;
  text-align: center;
}

.back-btn {
  background: white !important;
  color: #28a745 !important;
  border: 2px solid #28a745 !important;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  border-radius: 50px !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.back-btn:hover {
  background: #28a745 !important;
  color: white !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3) !important;
}

.back-btn i {
  margin-right: 8px;
  font-size: 1.2rem;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.2rem;
  }
  
  .privacy-content,
  .cookie-section,
  .compliance-section {
    padding: 60px 0;
  }
  
  .privacy-section {
    padding: 24px 20px;
    margin-bottom: 30px;
  }
  
  .section-title,
  .cookie-title,
  .compliance-title {
    font-size: 1.8rem;
  }
  
  .section-description,
  .cookie-intro,
  .compliance-intro {
    font-size: 1rem;
  }
  
  .privacy-hero {
    padding: 60px 0 40px;
  }
  
  .back-section {
    padding: 40px 0;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 1.8rem;
  }
  
  .hero-icon {
    width: 60px;
    height: 60px;
  }
  
  .hero-icon i {
    font-size: 2rem;
  }
  
  .privacy-section {
    padding: 20px 16px;
  }
  
  .cookie-title,
  .compliance-title {
    font-size: 1.6rem;
  }
  
  .back-btn {
    padding: 14px 28px !important;
    font-size: 1rem !important;
  }
}
</style>
